import type { MangaItem } from '../../types';
import { useAutoSetThumbnail } from '../../hooks/useAutoSetThumbnail';
import { getTagLabel, getTagColorClass } from '../../utils/tagUtils';

interface MangaCardProps {
  item: MangaItem;
  onClick: () => void;
  isSelectMode?: boolean;
  isSelected?: boolean;
  onSelect?: (id: string) => void;
}

export default function MangaCard({ item, onClick, isSelectMode, isSelected, onSelect }: MangaCardProps) {
  const { ref: thumbRef, displayUrl } = useAutoSetThumbnail(item);
  const thumbnailUrl = displayUrl ?? item.thumbnail_url;

  const handleClick = () => {
    if (isSelectMode) {
      onSelect?.(item.id);
    } else {
      onClick();
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`bg-white rounded-lg shadow hover:shadow-lg transition cursor-pointer overflow-hidden${isSelected ? ' ring-2 ring-red-500' : ''}`}
    >
      {/* モバイル: flex横並び / PC: block縦積み */}
      <div className="flex md:block">
        {/* サムネイル: モバイルは固定幅、PCはaspect比 */}
        <div ref={thumbRef} className="relative w-20 flex-shrink-0 bg-gray-200 flex items-center justify-center md:w-auto md:aspect-[2/3]">
          {isSelectMode && (
            <div className="absolute left-1 top-1 z-20">
              <input
                type="checkbox"
                checked={!!isSelected}
                onChange={() => onSelect?.(item.id)}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4 accent-red-600"
              />
            </div>
          )}
          {item.is_inactive && (
            <div className="absolute right-1 top-1 rounded bg-pink-100 px-1 py-0.5 text-[9px] font-semibold tracking-wide text-pink-700 md:right-2 md:top-2 md:text-[10px] z-10">
              INACTIVE
            </div>
          )}
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={item.title}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <span className="text-gray-400 text-xs md:text-sm">No Cover</span>
          )}
        </div>
        {/* テキスト情報 */}
        <div className="p-3 flex-1 min-w-0">
          <h3 className="font-bold text-sm truncate" title={item.title}>
            {item.title}
          </h3>
          <p className="text-xs text-gray-600 truncate" title={item.author}>
            {item.author}
          </p>
          {item.tags && item.tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              
              {item.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag.id}
                  className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] ${getTagColorClass(tag.name)}`}
                  title={getTagLabel(tag.name)}
                >
                  {getTagLabel(tag.name)}
                </span>
              ))}
              {item.tags.length > 3 && (
                <span className="text-[10px] text-gray-400">
                  +{item.tags.length - 3}
                </span>
              )}
            </div>
          )}
          <div className="flex items-center gap-1 mt-2">
            {[...Array(5)].map((_, i) => (
              <span
                key={i}
                className={`text-sm ${
                  i < item.rating ? 'text-yellow-500' : 'text-gray-300'
                }`}
              >
                ★
              </span>
            ))}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {item.page_count} pages
          </div>
        </div>
      </div>
    </div>
  );
}
